import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import inventoryApi from '../../api/inventoryApi';

// Only change the fetchInventories thunk
export const fetchInventories = createAsyncThunk(
  'inventory/fetchAll',
  async ({ page = 1, pageSize = 10, sub_category = '', ordering = '', search = '' }, { rejectWithValue }) => {
    try {
      const res = await inventoryApi.getAll(page, pageSize, sub_category, ordering, search);
      return res;
    } catch (e) {
      return rejectWithValue(e.response?.data?.detail || 'Failed to load inventory');
    }
  }
);

export const fetchInventoryById = createAsyncThunk(
  'inventory/fetchById',
  async (id, { rejectWithValue }) => {
    try {
      const res = await inventoryApi.getById(id);
      return res;
    } catch (e) {
      return rejectWithValue(e.response?.data || 'Failed to load details');
    }
  }
);

export const createInventory = createAsyncThunk(
  'inventory/create',
  async (data, { rejectWithValue }) => {
    try {
      const res = await inventoryApi.create(data);
      return res;
    } catch (e) {
      return rejectWithValue(e.response?.data || 'Create failed');
    }
  }
);

export const updateInventory = createAsyncThunk(
  'inventory/update',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const res = await inventoryApi.update(id, data);
      return res;
    } catch (e) {
      return rejectWithValue(e.response?.data || 'Update failed');
    }
  }
);

export const deleteInventory = createAsyncThunk(
  'inventory/delete',
  async (id, { rejectWithValue }) => {
    try {
      await inventoryApi.delete(id);
      return id;
    } catch (e) {
      return rejectWithValue(e.response?.data?.detail || 'Delete failed');
    }
  }
);

/* -------------------------- EXPORT -------------------------- */
export const exportInventories = createAsyncThunk(
  'inventory/export',
  async (_, { rejectWithValue }) => {
    try {
      const blob = await inventoryApi.exportCsv();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `inventory_${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      return rejectWithValue('Export failed');
    }
  }
);

/* -------------------------- IMPORT PREVIEW -------------------------- */
export const importPreview = createAsyncThunk(
  'inventory/importPreview',
  async (file, { rejectWithValue }) => {
    try {
      const data = await inventoryApi.importPreview(file);
      return data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.detail || 'Preview failed');
    }
  }
);

/* -------------------------- IMPORT CONFIRM -------------------------- */
export const importConfirm = createAsyncThunk(
  'inventory/importConfirm',
  async (preview, { rejectWithValue, dispatch }) => {
    try {
      await inventoryApi.importConfirm(preview);
      dispatch(fetchInventories({ page: 1, pageSize: 100 }));
    } catch (err) {
      return rejectWithValue(err.response?.data?.detail || 'Import failed');
    }
  }
);

const inventorySlice = createSlice({
  name: 'inventory',
  initialState: {
    items: [],
    selectedItem: null,
    pagination: { count: 0, page_size: 10, next: null, previous: null },
    isLoading: false,
    isLoadingDetails: false,
    error: null,
    importPreview: null,
    importTotal: null,
  },
  reducers: {
    clearError: (s) => { s.error = null; },
    clearSelectedItem: (s) => { s.selectedItem = null; },
  },
  extraReducers: (b) => {
    // FETCH ALL
    b.addCase(fetchInventories.pending, (s) => { s.isLoading = true; })
      .addCase(fetchInventories.fulfilled, (s, a) => {
        s.isLoading = false;
        s.items = a.payload.data;
        s.pagination = a.payload.pagination;
      })
      .addCase(fetchInventories.rejected, (s, a) => {
        s.isLoading = false;
        s.error = a.payload;
      });

    // FETCH BY ID
    b.addCase(fetchInventoryById.pending, (s) => { s.isLoadingDetails = true; })
      .addCase(fetchInventoryById.fulfilled, (s, a) => {
        s.isLoadingDetails = false;
        s.selectedItem = a.payload;
      })
      .addCase(fetchInventoryById.rejected, (s, a) => {
        s.isLoadingDetails = false;
        s.error = a.payload;
      });

    // CREATE
    b.addCase(createInventory.fulfilled, (s, a) => {
      s.items.unshift(a.payload);
      s.pagination.count += 1;
    });

    // UPDATE
    b.addCase(updateInventory.fulfilled, (s, a) => {
      const idx = s.items.findIndex(i => i.id === a.payload.id);
      if (idx !== -1) s.items[idx] = a.payload;
    });

    // DELETE
    b.addCase(deleteInventory.fulfilled, (s, a) => {
      s.items = s.items.filter(i => i.id !== a.payload);
      s.pagination.count -= 1;
    });

    // EXPORT
    b.addCase(exportInventories.rejected, (s, a) => {
      s.error = a.payload;
    });

    // IMPORT PREVIEW
    b.addCase(importPreview.pending, (s) => { s.isLoading = true; })
      .addCase(importPreview.fulfilled, (s, a) => {
        s.isLoading = false;
        s.importPreview = a.payload.preview;
        s.importTotal = a.payload.total;
      })
      .addCase(importPreview.rejected, (s, a) => {
        s.isLoading = false;
        s.error = a.payload;
      });

    // IMPORT CONFIRM
    b.addCase(importConfirm.pending, (s) => { s.isLoading = true; })
      .addCase(importConfirm.fulfilled, (s) => {
        s.isLoading = false;
        s.importPreview = null;
        s.importTotal = null;
      })
      .addCase(importConfirm.rejected, (s, a) => {
        s.isLoading = false;
        s.error = a.payload;
      });
  },
});

export const { clearError, clearSelectedItem } = inventorySlice.actions;
export default inventorySlice.reducer;